import {useFirebaseState} from "./firebaseContext";
import {useEffect, useState, useCallback} from "react";
import {collection, getDocs, doc, addDoc, updateDoc, deleteDoc, orderBy, query} from "firebase/firestore";
import {DEFAULT_TESTIMONIALS} from "../testimonials/defaultTestimonials";

export const useTestimonials = ({forceRefresh} = {}) => {
    const [testimonials, setTestimonials] = useState(DEFAULT_TESTIMONIALS);
    const [loading, setLoading] = useState(true);
    let db = null;
    try {
        const firebaseState = useFirebaseState();
        db = firebaseState?.db || null;
    } catch {
        db = null;
    }

    const fetchTestimonials = useCallback(async () => {
        if (!db) {
            setLoading(false);
            return;
        }

        try {
            const testimonialsRef = collection(db, "testimonials");
            const q = query(testimonialsRef, orderBy("order", "asc"));
            const querySnapshot = await getDocs(q);

            if (querySnapshot && !querySnapshot.empty) {
                const items = [];
                querySnapshot.forEach((docSnap) => {
                    items.push({id: docSnap.id, ...docSnap.data()});
                });
                setTestimonials(items);
            } else {
                setTestimonials(DEFAULT_TESTIMONIALS);
            }
        } catch {
            // Fallback to default testimonials if collection isn't yet created or indexed
            setTestimonials(DEFAULT_TESTIMONIALS);
        } finally {
            setLoading(false);
        }
    }, [db]);

    useEffect(() => {
        fetchTestimonials();
    }, [fetchTestimonials, forceRefresh]);

    const saveTestimonial = async (data) => {
        if (!db) return;
        const now = new Date();
        const payload = {
            title: data.title || "",
            quote: data.quote || "",
            author: data.author || "",
            role: data.role || "",
            organization: data.organization || "",
            order: Number(data.order) || 1,
            published: data.published !== false,
            updatedAt: now,
        };

        if (data.id && !data.id.startsWith("testimonial-")) {
            const ref = doc(db, "testimonials", data.id);
            await updateDoc(ref, payload);
            await fetchTestimonials();
            return data.id;
        } else {
            const collectionRef = collection(db, "testimonials");
            const newDocRef = await addDoc(collectionRef, {
                ...payload,
                createdAt: now,
            });
            await fetchTestimonials();
            return newDocRef?.id || "new-id";
        }
    };

    const removeTestimonial = async (id) => {
        if (!db || !id) return;
        if (!id.startsWith("testimonial-")) {
            const ref = doc(db, "testimonials", id);
            await deleteDoc(ref);
        }
        await fetchTestimonials();
    };

    return {
        testimonials,
        loading,
        fetchTestimonials,
        saveTestimonial,
        removeTestimonial,
    };
};
